import { ApplicationVersion } from '../entities/application.entity'

export enum VersionActionType {
  ContinueMerge = 'continueMerge',
  DiscardMerge = 'discardMerge',
  CommitChanges = 'commitChanges',
  CreateBuild = 'createBuild',
  Merge = 'merge',
  StartNewVersion = 'startNewVersion',
  CreateNewBranch = 'createNewBranch',
  ExportVersion = 'exportVersion',
  DiscardChanges = 'discardChanges',
  ArchiveBranch = 'archiveBranch',
  UnarchiveBranch = 'unarchiveBranch',
  EditCommitMessage = 'editCommitMessage',
  CompareVersions = 'compareVersions',
  ViewCommitDetails = 'viewCommitDetails',
}

export type VersionActionConfig = {
  action: VersionActionType
  label: string
  visible: boolean
  enabled: boolean
  /** flx-icon name (or ph class for menu-rendered actions); null = the consuming template renders a component instead of an icon */
  icon: string | null
  iconClass?: string
  style?: 'primary' | 'secondary' | 'secondary danger'
  tooltip?: string
  /** false = half-width, sharing a grid row with the adjacent half-width action; defaults to a full row */
  fullWidth?: boolean
  testId: string
  /** Display position in action lists — consumers sort by this (like OrderedMenuItem.order) */
  order: number
}

export type VersionActionsMap = Record<VersionActionType, VersionActionConfig>

/** Named to keep `visible`/`enabled` unswappable at call sites — both are booleans */
type ActionState = {
  visible: boolean
  enabled: boolean
}

export type VersionActionEvent =
  | {
      action: VersionActionType.ArchiveBranch
      payload: { appVersion: ApplicationVersion; reason: string }
    }
  | { action: VersionActionType.UnarchiveBranch; payload: ApplicationVersion }
  | {
      action: Exclude<
        VersionActionType,
        VersionActionType.ArchiveBranch | VersionActionType.UnarchiveBranch
      >
      payload?: never
    }

export const ContinueMergeAction = ({ visible, enabled }: ActionState): VersionActionConfig => ({
  action: VersionActionType.ContinueMerge,
  label: 'Resolve Conflicts',
  visible,
  enabled,
  icon: null,
  style: 'secondary',
  fullWidth: false,
  testId: 'continue-merge-button',
  order: 10,
})

export const DiscardMergeAction = ({ visible, enabled }: ActionState): VersionActionConfig => ({
  action: VersionActionType.DiscardMerge,
  label: 'Discard Merge',
  visible,
  enabled,
  icon: null,
  style: 'secondary danger',
  fullWidth: false,
  testId: 'discard-merge-button',
  order: 20,
})

export const CommitChangesAction = ({ visible, enabled }: ActionState): VersionActionConfig => ({
  action: VersionActionType.CommitChanges,
  label: 'Commit Changes',
  visible,
  enabled,
  icon: null,
  style: 'secondary',
  fullWidth: false,
  testId: 'submit-changes-button',
  order: 30,
})

export const CreateBuildAction = ({ visible, enabled }: ActionState): VersionActionConfig => ({
  action: VersionActionType.CreateBuild,
  label: 'Create Build...',
  visible,
  enabled,
  icon: 'ph ph-hammer',
  style: 'secondary',
  testId: 'create-build-button',
  order: 40,
})

export const MergeAction = ({
  visible,
  enabled,
  disabledReason = 'Commit the draft version before merging',
}: ActionState & { disabledReason?: string }): VersionActionConfig => ({
  action: VersionActionType.Merge,
  label: 'Merge Branch',
  visible,
  enabled,
  icon: null,
  style: 'secondary',
  tooltip: disabledReason,
  testId: 'merge-branch-button',
  order: 50,
})

export const StartNewVersionAction = ({
  visible,
  enabled,
  disabledReason,
}: ActionState & { disabledReason?: string }): VersionActionConfig => ({
  action: VersionActionType.StartNewVersion,
  label: 'Start New Version',
  visible,
  enabled,
  icon: null,
  style: 'secondary',
  fullWidth: false,
  tooltip: disabledReason,
  testId: 'start-new-version-button',
  order: 60,
})

export const CreateNewBranchAction = ({ visible, enabled }: ActionState): VersionActionConfig => ({
  action: VersionActionType.CreateNewBranch,
  label: 'Create Branch',
  visible,
  enabled,
  icon: null,
  style: 'secondary',
  fullWidth: false,
  testId: 'create-new-branch-button',
  order: 70,
})

export const ExportVersionAction = ({ visible, enabled }: ActionState): VersionActionConfig => ({
  action: VersionActionType.ExportVersion,
  label: 'Export Version',
  visible,
  enabled,
  icon: 'ph ph-export',
  style: 'secondary',
  testId: 'export-version-button',
  order: 80,
})

export const DiscardChangesAction = ({ visible, enabled }: ActionState): VersionActionConfig => ({
  action: VersionActionType.DiscardChanges,
  label: 'Delete Draft',
  visible,
  enabled,
  icon: null,
  style: 'secondary danger',
  fullWidth: false,
  testId: 'discard-changes-button',
  order: 100,
})

export const ArchiveBranchAction = ({
  visible,
  enabled,
  blockReason,
}: ActionState & { blockReason?: string }): VersionActionConfig => ({
  action: VersionActionType.ArchiveBranch,
  label: 'Archive Branch',
  visible,
  enabled,
  icon: 'ph ph-archive',
  style: 'secondary danger',
  tooltip: blockReason,
  testId: 'archive-branch-menu-item',
  order: 110,
})

export const UnarchiveBranchAction = ({ visible, enabled }: ActionState): VersionActionConfig => ({
  action: VersionActionType.UnarchiveBranch,
  label: 'Unarchive Branch',
  visible,
  enabled,
  icon: 'ph ph-arrow-counter-clockwise',
  style: 'secondary',
  testId: 'unarchive-branch-button',
  order: 120,
})

export const EditCommitMessageAction = ({
  visible,
  enabled,
}: ActionState): VersionActionConfig => ({
  action: VersionActionType.EditCommitMessage,
  label: 'Rename Commit Message',
  visible,
  enabled,
  icon: 'ph ph-pencil-simple',
  style: 'secondary',
  testId: 'edit-message-version-id-btn',
  order: 130,
})

export const CompareVersionsAction = ({ visible, enabled }: ActionState): VersionActionConfig => ({
  action: VersionActionType.CompareVersions,
  label: 'Compare versions',
  visible,
  enabled,
  icon: 'ph ph-git-diff',
  style: 'secondary',
  testId: 'compare-versions-button',
  order: 5,
})

export const ViewCommitDetailsAction = ({
  visible,
  enabled,
}: ActionState): VersionActionConfig => ({
  action: VersionActionType.ViewCommitDetails,
  label: 'Commit details',
  visible,
  enabled,
  icon: 'ph ph-chat-text',
  style: 'secondary',
  testId: 'view-commit-details-button',
  order: 135,
})
